/**
 * Parfumoo Filter — Storefront Engine
 * High-performance native collection filtering and predictive search
 * Designed specifically for Parfumoo Luxury Fragrances
 */

(function () {
  'use strict';

  // 1. Check if App is Active
  const isAppActive = localStorage.getItem('PARFUMOO_FILTER_ACTIVE') !== 'false' && localStorage.getItem('SPF_APP_ACTIVE') !== 'false';
  if (!isAppActive) {
    console.log('[Parfumoo Filter] App is currently deactivated by merchant.');
    return;
  }

  // 2. Custom Configuration & Labels (From Shopify Theme Customizer or App Admin)
  function getFilterConfig() {
    let themeConfig = {};
    const configEl = document.getElementById('parfumoo-filter-config');
    if (configEl) {
      try {
        themeConfig = JSON.parse(configEl.textContent || '{}');
      } catch (e) {
        console.warn('[Parfumoo Filter] Error parsing filter config:', e);
      }
    }

    let localLabels = {};
    try {
      localLabels = JSON.parse(localStorage.getItem('PARFUMOO_FILTER_LABELS') || localStorage.getItem('SPF_FILTER_LABELS') || '{}');
    } catch (e) {}

    return {
      show_brand: themeConfig.show_brand !== false,
      label_brand: themeConfig.label_brand || localLabels.brand || 'Brand',

      show_category: themeConfig.show_category !== false,
      label_category: themeConfig.label_category || localLabels.category || 'Category',

      show_gender: themeConfig.show_gender !== false,
      label_gender: themeConfig.label_gender || localLabels.gender || 'Gender',

      show_fragrance_family: themeConfig.show_fragrance_family !== false,
      label_fragrance_family: themeConfig.label_fragrance_family || localLabels.fragrance_family || 'Fragrance Family',

      show_material: themeConfig.show_material !== false,
      label_material: themeConfig.label_material || localLabels.material || 'Material',

      show_dispenser_type: themeConfig.show_dispenser_type !== false,
      label_dispenser_type: themeConfig.label_dispenser_type || localLabels.dispenser_type || 'Dispenser Type',

      show_availability: themeConfig.show_availability !== false,
      label_availability: themeConfig.label_availability || localLabels.availability || 'Availability',

      show_price: themeConfig.show_price !== false,
      label_price: themeConfig.label_price || localLabels.price || 'Price'
    };
  }

  // 3. Normalized gender resolver (handles multi-values like 'Male, Female')
  function getProductGenders(genderStr, title, tags) {
    const list = [];
    const raw = String(genderStr || '').toLowerCase();
    const tl = String(title || '').toLowerCase();
    const tagStr = Array.isArray(tags) ? tags.join(' ').toLowerCase() : String(tags || '').toLowerCase();
    const combined = `${raw} ${tl} ${tagStr}`;

    if (combined.includes('women') || combined.includes('female') || combined.includes('pour femme') || combined.includes('for women') || combined.includes('for her') || combined.includes('femme')) {
      list.push('Women');
    }
    if (combined.includes('men') || combined.includes('male') || combined.includes('pour homme') || combined.includes('for men') || combined.includes('for him') || combined.includes('homme')) {
      list.push('Men');
    }
    if (combined.includes('unisex')) {
      if (!list.includes('Unisex')) list.push('Unisex');
    }
    if (list.length === 0) {
      list.push('Unisex');
    }
    return list;
  }

  // 4. Known brand dictionary
  const KNOWN_BRANDS = [
    'Carolina Herrera', 'Davidoff', 'Arome', 'Srmani', 'Klein', 'Perfume',
    'Prada', 'Armaf', 'Riiffs', 'NEESH', 'Mykonos', 'Spagnolo', 'Versace',
    'Dior', 'Christian Dior', 'Chanel', 'Lattafa', 'Afnan', 'Ajmal',
    'Burberry', 'Bvlgari', 'Calvin Klein', 'Clinique', 'Coach', 'Diesel',
    'Dolce & Gabbana', 'Dunhill', 'Elie Saab', 'Elizabeth Arden', 'Ferrari',
    'Giorgio Armani', 'Givenchy', 'Gucci', 'Guerlain', 'Hermes', 'Hugo Boss',
    'Issey Miyake', 'Jean Paul Gaultier', 'Jimmy Choo', 'Jo Malone', 'Kenzo',
    'Lancome', 'Mancera', 'Montblanc', 'Mugler', 'Narciso Rodriguez',
    'Paco Rabanne', 'Tom Ford', 'Valentino', 'Yves Saint Laurent'
  ];

  // 5. Popular Search Suggestions
  const POPULAR_SUGGESTIONS = [
    'Prada Luna Rossa', 'Davidoff Cool Water', 'Armaf Club De Nuit',
    'Carolina Herrera 212', 'Eau De Parfum', 'Woody Perfume',
    'Fresh Aquatic', 'Extrait De Parfum', 'Amber Floral', 'Perfume For Men',
    'Perfume For Women'
  ];

  /* -------------------------------------------------------------
   * Collection Filter State & Cache
   * ------------------------------------------------------------- */
  const activeFilters = {
    brand: [],
    category: [],
    gender: [],
    fragrance_family: [],
    material: [],
    dispenser_type: [],
    product_form: [],
    availability: [],
    max_price: null
  };

  let currentCollectionProducts = [];

  function getCardData(card) {
    const link = card.querySelector('a[href*="/products/"]');
    const href = link ? link.getAttribute('href') : '';
    const handleMatch = href.match(/\/products\/([a-zA-Z0-9_-]+)/);
    const handle = (handleMatch ? handleMatch[1] : (card.getAttribute('data-product-handle') || '')).toLowerCase();

    const titleEl = card.querySelector('.product-grid-item__title, .card__heading, h3, a.product__media_thumbnail img[alt]');
    let title = '';
    if (titleEl) {
      title = (titleEl.getAttribute('alt') || titleEl.innerText || '').trim();
    }

    const priceEl = card.querySelector('.price-item--regular, .price, .money, .price__regular');
    let price = 0;
    if (priceEl) {
      const priceNum = priceEl.innerText.replace(/[^0-9.]/g, '');
      if (priceNum) price = parseFloat(priceNum);
    }

    // Read real-time live metadata embedded directly by Liquid
    const metaTag = card.querySelector('.parfumoo-filter-meta');
    let liveMeta = null;
    if (metaTag) {
      try {
        liveMeta = JSON.parse(metaTag.textContent);
      } catch (e) {
        console.warn('[Parfumoo Filter] Error parsing DOM meta:', e);
      }
    }

    let meta = null;
    if (liveMeta) {
      let brand = liveMeta.vendor || '';
      if (!brand || brand.toUpperCase() === 'PARFUMOO') {
        for (const k of KNOWN_BRANDS) {
          if (title.toLowerCase().includes(k.toLowerCase())) {
            brand = k;
            break;
          }
        }
        if (!brand || brand.toUpperCase() === 'PARFUMOO') {
          brand = title.split(' ')[0] || 'Parfumoo';
        }
      }

      meta = {
        handle: liveMeta.handle || handle,
        title: liveMeta.title || title,
        brand: brand,
        gender: liveMeta.gender || '',
        category: (liveMeta.collections && liveMeta.collections.length > 0) ? liveMeta.collections[0] : (liveMeta.product_form || liveMeta.type || ''),
        collections: Array.isArray(liveMeta.collections) ? liveMeta.collections : [],
        fragrance_family: liveMeta.fragrance_family || liveMeta.fragrance || '',
        product_form: liveMeta.product_form || liveMeta.type || '',
        material: liveMeta.material || '',
        dispenser_type: liveMeta.dispenser_type || '',
        color: liveMeta.color || '',
        price: liveMeta.price ? (liveMeta.price / 100) : price,
        in_stock: liveMeta.available !== false,
        tags: liveMeta.tags || []
      };
    } else {
      meta = normalizeFallbackProduct({ handle, title, price });
    }

    return { card, handle, title, price, meta };
  }

  function normalizeFallbackProduct(p) {
    const handle = p.handle || '';
    const title = p.title || '';
    let brand = p.vendor || '';

    if (!brand || brand.toUpperCase() === 'PARFUMOO') {
      for (const k of KNOWN_BRANDS) {
        if (title.toLowerCase().includes(k.toLowerCase())) {
          brand = k;
          break;
        }
      }
      if (!brand || brand.toUpperCase() === 'PARFUMOO') {
        brand = title.split(' ')[0] || 'Parfumoo';
      }
    }

    const tl = title.toLowerCase();
    let gender = 'Unisex';
    if (tl.includes('women') || tl.includes('femme')) gender = 'Women';
    else if (tl.includes('men') || tl.includes('homme')) gender = 'Men';

    let category = 'Eau De Parfum';
    if (tl.includes('extrait')) category = 'Extrait De Parfum';
    else if (tl.includes('edt') || tl.includes('eau de toilette')) category = 'Eau De Toilette';

    return {
      handle,
      title,
      brand,
      gender,
      category,
      fragrance_family: category.includes('Parfum') ? 'Floral / Woody' : 'Fresh Aquatic',
      product_form: category,
      material: '',
      dispenser_type: '',
      color: '',
      price: p.price || 0,
      in_stock: true,
      tags: []
    };
  }

  /* -------------------------------------------------------------
   * Collection Page Filtering
   * ------------------------------------------------------------- */
  async function initCollectionFilters() {
    const isCollectionPage = window.location.pathname.includes('/collections');
    const isSearchPage = window.location.pathname.includes('/search');
    if (!isCollectionPage && !isSearchPage) return;

    // Scan DOM cards immediately to extract live metadata
    const cards = document.querySelectorAll(
      '#product-grid .col, [data-parfumoo-filter-items] .col, [data-globo-filter-items] .col, .product-grid .col, .grid__item, [data-product-column] > .col'
    );
    currentCollectionProducts = Array.from(cards).map(getCardData).map(d => d.meta).filter(Boolean);

    renderAllFilterContainers();
    applyFiltersToGrid();
  }

  function renderAllFilterContainers() {
    const desktopContainer = document.getElementById('FacetsWrapperDesktop') || document.querySelector('.facets-wrapper') || document.querySelector('.facets__form-vertical');
    if (desktopContainer) {
      renderFilterTree(desktopContainer, 'desktop', currentCollectionProducts);
    }

    const mobileContainer = document.querySelector('.mobile-facets__main') || document.getElementById('FacetFiltersFormMobile');
    if (mobileContainer) {
      renderFilterTree(mobileContainer, 'mobile', currentCollectionProducts);
    }
  }

  function renderFilterTree(container, layoutType, products) {
    products = products || [];
    const config = getFilterConfig();
    const prefix = layoutType === 'mobile' ? 'mob_' : 'desk_';

    // 1. Dynamic Brands
    const brandCounts = {};
    products.forEach(p => {
      if (p.brand) {
        brandCounts[p.brand] = (brandCounts[p.brand] || 0) + 1;
      }
    });
    const brands = Object.keys(brandCounts).sort();

    // 2. Dynamic Categories (Shopify Collections)
    const categoryCounts = {};
    products.forEach(p => {
      const pCols = (Array.isArray(p.collections) && p.collections.length > 0)
        ? p.collections
        : (p.category ? [p.category] : []);
      pCols.forEach(colName => {
        if (colName && typeof colName === 'string' && colName.trim()) {
          const c = colName.trim();
          categoryCounts[c] = (categoryCounts[c] || 0) + 1;
        }
      });
    });
    const categories = Object.keys(categoryCounts).sort();

    // 3. Dynamic Gender (Supports multi-values: Men, Women, Unisex)
    const genderCounts = { Men: 0, Women: 0, Unisex: 0 };
    products.forEach(p => {
      const gList = getProductGenders(p.gender, p.title, p.tags);
      gList.forEach(g => {
        if (genderCounts[g] !== undefined) {
          genderCounts[g]++;
        }
      });
    });
    const genders = ['Men', 'Women', 'Unisex'].filter(g => (genderCounts[g] || 0) > 0);

    // 4. Dynamic Fragrance Families
    const fragCounts = {};
    products.forEach(p => {
      if (p.fragrance_family) {
        const parts = p.fragrance_family.split(',').map(s => s.trim()).filter(Boolean);
        parts.forEach(part => {
          fragCounts[part] = (fragCounts[part] || 0) + 1;
        });
      }
    });
    const fragranceFamilies = Object.keys(fragCounts).sort();

    // 5. Dynamic Material
    const materialCounts = {};
    products.forEach(p => {
      if (p.material) {
        materialCounts[p.material] = (materialCounts[p.material] || 0) + 1;
      }
    });
    const materials = Object.keys(materialCounts).sort();

    // 6. Dynamic Dispenser Type
    const dispenserCounts = {};
    products.forEach(p => {
      if (p.dispenser_type) {
        dispenserCounts[p.dispenser_type] = (dispenserCounts[p.dispenser_type] || 0) + 1;
      }
    });
    const dispensers = Object.keys(dispenserCounts).sort();

    // 7. Dynamic Availability
    const inStockCount = products.filter(p => p.in_stock).length;
    const outOfStockCount = products.filter(p => !p.in_stock).length;

    // 8. Dynamic Price
    const prices = products.map(p => p.price).filter(p => p > 0);
    const minPrice = prices.length ? Math.floor(Math.min(...prices)) : 0;
    const maxPrice = prices.length ? Math.ceil(Math.max(...prices)) : 1000;
    const currentMaxPrice = activeFilters.max_price !== null ? activeFilters.max_price : maxPrice;

    let html = `
      <div class="sfs-app-filter-tree sfs-layout-${layoutType}" style="${layoutType === 'desktop' ? 'padding-right: 20px;' : 'padding: 15px;'}">
        <!-- Active Filter Chips -->
        <div id="${prefix}sfs-chips-area" class="sfs-active-filters-bar" style="display:none;"></div>
    `;

    // BRAND
    if (config.show_brand && brands.length > 0) {
      html += `
        <details class="single__widget" open>
          <summary>
            <span class="sfs-filter-heading">${escapeHtml(config.label_brand)}</span>
            <svg class="sfs-filter-chevron" viewBox="0 0 12 7" fill="none"><path d="M1 1L6 6L11 1" stroke="#222" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </summary>
          <div>
            <ul class="sfs-facet-list">
              ${brands.map(b => {
                const count = brandCounts[b] || 0;
                const checked = activeFilters.brand.includes(b) ? 'checked' : '';
                return `
                  <li class="sfs-facet-item" style="font-size:16px; padding:7px 0; display:flex; align-items:center;">
                    <label class="sfs-facet-label" style="font-size:16px; cursor:pointer; display:inline-flex; align-items:center; gap:10px; width:100%;">
                      <input type="checkbox" class="sfs-facet-checkbox" style="width:17px; height:17px; accent-color:#000; margin:0; cursor:pointer;" data-group="brand" data-val="${escapeHtml(b)}" ${checked} />
                      <span class="sfs-facet-text" style="font-size:16px; color:#222;">${escapeHtml(b)} (${count})</span>
                    </label>
                  </li>
                `;
              }).join('')}
            </ul>
          </div>
        </details>
      `;
    }

    // CATEGORY
    if (config.show_category && categories.length > 0) {
      html += `
        <details class="single__widget" open>
          <summary>
            <span class="sfs-filter-heading">${escapeHtml(config.label_category)}</span>
            <svg class="sfs-filter-chevron" viewBox="0 0 12 7" fill="none"><path d="M1 1L6 6L11 1" stroke="#222" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </summary>
          <div>
            <ul class="sfs-facet-list">
              ${categories.map(c => {
                const count = categoryCounts[c] || 0;
                const checked = activeFilters.category.includes(c) ? 'checked' : '';
                return `
                  <li class="sfs-facet-item" style="font-size:16px; padding:7px 0; display:flex; align-items:center;">
                    <label class="sfs-facet-label" style="font-size:16px; cursor:pointer; display:inline-flex; align-items:center; gap:10px; width:100%;">
                      <input type="checkbox" class="sfs-facet-checkbox" style="width:17px; height:17px; accent-color:#000; margin:0; cursor:pointer;" data-group="category" data-val="${escapeHtml(c)}" ${checked} />
                      <span class="sfs-facet-text" style="font-size:16px; color:#222;">${escapeHtml(c)} (${count})</span>
                    </label>
                  </li>
                `;
              }).join('')}
            </ul>
          </div>
        </details>
      `;
    }

    // AVAILABILITY
    if (config.show_availability && (inStockCount > 0 || outOfStockCount > 0)) {
      html += `
        <details class="single__widget">
          <summary>
            <span class="sfs-filter-heading">${escapeHtml(config.label_availability)}</span>
            <svg class="sfs-filter-chevron" viewBox="0 0 12 7" fill="none"><path d="M1 1L6 6L11 1" stroke="#222" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </summary>
          <div>
            <ul class="sfs-facet-list">
              ${inStockCount > 0 ? `
              <li class="sfs-facet-item" style="font-size:16px; padding:7px 0; display:flex; align-items:center;">
                <label class="sfs-facet-label" style="font-size:16px; cursor:pointer; display:inline-flex; align-items:center; gap:10px; width:100%;">
                  <input type="checkbox" class="sfs-facet-checkbox" style="width:17px; height:17px; accent-color:#000; margin:0; cursor:pointer;" data-group="availability" data-val="in_stock" ${activeFilters.availability.includes('in_stock') ? 'checked' : ''} />
                  <span class="sfs-facet-text" style="font-size:16px; color:#222;">In Stock (${inStockCount})</span>
                </label>
              </li>` : ''}
              ${outOfStockCount > 0 ? `
              <li class="sfs-facet-item" style="font-size:16px; padding:7px 0; display:flex; align-items:center;">
                <label class="sfs-facet-label" style="font-size:16px; cursor:pointer; display:inline-flex; align-items:center; gap:10px; width:100%;">
                  <input type="checkbox" class="sfs-facet-checkbox" style="width:17px; height:17px; accent-color:#000; margin:0; cursor:pointer;" data-group="availability" data-val="out_of_stock" ${activeFilters.availability.includes('out_of_stock') ? 'checked' : ''} />
                  <span class="sfs-facet-text" style="font-size:16px; color:#222;">Out of Stock (${outOfStockCount})</span>
                </label>
              </li>` : ''}
            </ul>
          </div>
        </details>
      `;
    }

    // GENDER (Dynamically reflects Shopify Category Metafield!)
    if (config.show_gender && genders.length > 0) {
      html += `
        <details class="single__widget" open>
          <summary>
            <span class="sfs-filter-heading">${escapeHtml(config.label_gender)}</span>
            <svg class="sfs-filter-chevron" viewBox="0 0 12 7" fill="none"><path d="M1 1L6 6L11 1" stroke="#222" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </summary>
          <div>
            <ul class="sfs-facet-list">
              ${genders.map(g => {
                const count = genderCounts[g] || 0;
                const checked = activeFilters.gender.includes(g) ? 'checked' : '';
                return `
                  <li class="sfs-facet-item" style="font-size:16px; padding:7px 0; display:flex; align-items:center;">
                    <label class="sfs-facet-label" style="font-size:16px; cursor:pointer; display:inline-flex; align-items:center; gap:10px; width:100%;">
                      <input type="checkbox" class="sfs-facet-checkbox" style="width:17px; height:17px; accent-color:#000; margin:0; cursor:pointer;" data-group="gender" data-val="${escapeHtml(g)}" ${checked} />
                      <span class="sfs-facet-text" style="font-size:16px; color:#222;">${escapeHtml(g)} (${count})</span>
                    </label>
                  </li>
                `;
              }).join('')}
            </ul>
          </div>
        </details>
      `;
    }

    // FRAGRANCE FAMILY
    if (config.show_fragrance_family && fragranceFamilies.length > 0) {
      html += `
        <details class="single__widget" open>
          <summary>
            <span class="sfs-filter-heading">${escapeHtml(config.label_fragrance_family)}</span>
            <svg class="sfs-filter-chevron" viewBox="0 0 12 7" fill="none"><path d="M1 1L6 6L11 1" stroke="#222" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </summary>
          <div>
            <ul class="sfs-facet-list">
              ${fragranceFamilies.map(f => {
                const count = fragCounts[f] || 0;
                const checked = activeFilters.fragrance_family.includes(f) ? 'checked' : '';
                return `
                  <li class="sfs-facet-item" style="font-size:16px; padding:7px 0; display:flex; align-items:center;">
                    <label class="sfs-facet-label" style="font-size:16px; cursor:pointer; display:inline-flex; align-items:center; gap:10px; width:100%;">
                      <input type="checkbox" class="sfs-facet-checkbox" style="width:17px; height:17px; accent-color:#000; margin:0; cursor:pointer;" data-group="fragrance_family" data-val="${escapeHtml(f)}" ${checked} />
                      <span class="sfs-facet-text" style="font-size:16px; color:#222;">${escapeHtml(f)} (${count})</span>
                    </label>
                  </li>
                `;
              }).join('')}
            </ul>
          </div>
        </details>
      `;
    }

    // MATERIAL (If any products have Material metafield)
    if (config.show_material && materials.length > 0) {
      html += `
        <details class="single__widget">
          <summary>
            <span class="sfs-filter-heading">${escapeHtml(config.label_material)}</span>
            <svg class="sfs-filter-chevron" viewBox="0 0 12 7" fill="none"><path d="M1 1L6 6L11 1" stroke="#222" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </summary>
          <div>
            <ul class="sfs-facet-list">
              ${materials.map(m => {
                const count = materialCounts[m] || 0;
                const checked = activeFilters.material.includes(m) ? 'checked' : '';
                return `
                  <li class="sfs-facet-item" style="font-size:16px; padding:7px 0; display:flex; align-items:center;">
                    <label class="sfs-facet-label" style="font-size:16px; cursor:pointer; display:inline-flex; align-items:center; gap:10px; width:100%;">
                      <input type="checkbox" class="sfs-facet-checkbox" style="width:17px; height:17px; accent-color:#000; margin:0; cursor:pointer;" data-group="material" data-val="${escapeHtml(m)}" ${checked} />
                      <span class="sfs-facet-text" style="font-size:16px; color:#222;">${escapeHtml(m)} (${count})</span>
                    </label>
                  </li>
                `;
              }).join('')}
            </ul>
          </div>
        </details>
      `;
    }

    // DISPENSER TYPE (If any products have Dispenser Type metafield)
    if (config.show_dispenser_type && dispensers.length > 0) {
      html += `
        <details class="single__widget">
          <summary>
            <span class="sfs-filter-heading">${escapeHtml(config.label_dispenser_type)}</span>
            <svg class="sfs-filter-chevron" viewBox="0 0 12 7" fill="none"><path d="M1 1L6 6L11 1" stroke="#222" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </summary>
          <div>
            <ul class="sfs-facet-list">
              ${dispensers.map(d => {
                const count = dispenserCounts[d] || 0;
                const checked = activeFilters.dispenser_type.includes(d) ? 'checked' : '';
                return `
                  <li class="sfs-facet-item" style="font-size:16px; padding:7px 0; display:flex; align-items:center;">
                    <label class="sfs-facet-label" style="font-size:16px; cursor:pointer; display:inline-flex; align-items:center; gap:10px; width:100%;">
                      <input type="checkbox" class="sfs-facet-checkbox" style="width:17px; height:17px; accent-color:#000; margin:0; cursor:pointer;" data-group="dispenser_type" data-val="${escapeHtml(d)}" ${checked} />
                      <span class="sfs-facet-text" style="font-size:16px; color:#222;">${escapeHtml(d)} (${count})</span>
                    </label>
                  </li>
                `;
              }).join('')}
            </ul>
          </div>
        </details>
      `;
    }

    // PRICE
    if (config.show_price && prices.length > 0) {
      html += `
        <details class="single__widget" open>
          <summary>
            <span class="sfs-filter-heading">${escapeHtml(config.label_price)}</span>
            <svg class="sfs-filter-chevron" viewBox="0 0 12 7" fill="none"><path d="M1 1L6 6L11 1" stroke="#222" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </summary>
          <div style="padding: 10px 0;">
            <div style="display:flex; justify-content:space-between; font-size:14px; margin-bottom:12px; font-weight:600;">
              <span>₹${minPrice.toLocaleString()}</span>
              <span class="sfs-price-display">Up to ₹${currentMaxPrice.toLocaleString()}</span>
            </div>
            <input type="range" class="sfs-price-slider" min="${minPrice}" max="${maxPrice}" step="50" value="${currentMaxPrice}" style="width:100%; accent-color:#000;" />
          </div>
        </details>
      `;
    }

    html += `</div>`;
    container.innerHTML = html;
    bindFilterEvents(container);
  }

  function bindFilterEvents(container) {
    container.querySelectorAll('input[type="checkbox"]').forEach(box => {
      box.addEventListener('change', (e) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
        const group = e.target.dataset.group;
        const val = e.target.dataset.val;

        if (e.target.checked) {
          if (!activeFilters[group].includes(val)) activeFilters[group].push(val);
        } else {
          activeFilters[group] = activeFilters[group].filter(v => v !== val);
        }

        document.querySelectorAll(`input[type="checkbox"][data-group="${group}"][data-val="${val}"]`).forEach(other => {
          other.checked = e.target.checked;
        });

        applyFiltersToGrid();
      });
    });

    const priceSlider = container.querySelector('.sfs-price-slider');
    const priceDisplay = container.querySelector('.sfs-price-display');
    if (priceSlider && priceDisplay) {
      priceSlider.addEventListener('input', (e) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
        const max = parseInt(e.target.value);
        document.querySelectorAll('.sfs-price-display').forEach(d => {
          d.innerText = `Up to ₹${max.toLocaleString()}`;
        });
        document.querySelectorAll('.sfs-price-slider').forEach(s => {
          s.value = max;
        });
        activeFilters.max_price = max;
        applyFiltersToGrid();
      });
    }
  }

  function applyFiltersToGrid() {
    renderActiveChips();

    const productCards = document.querySelectorAll(
      '#product-grid .col, [data-parfumoo-filter-items] .col, [data-globo-filter-items] .col, .product-grid .col, .grid__item, [data-product-column] > .col'
    );
    if (!productCards.length) return;

    let visibleCount = 0;

    productCards.forEach(card => {
      const data = getCardData(card);
      const meta = data.meta;
      const title = data.title.toLowerCase();

      let visible = true;

      // Brand filter
      if (activeFilters.brand.length > 0) {
        const brandMatch = activeFilters.brand.some(b => {
          const bl = b.toLowerCase();
          return title.includes(bl) || (meta && meta.brand && meta.brand.toLowerCase() === bl);
        });
        if (!brandMatch) visible = false;
      }

      // Gender filter (Multi-value support)
      if (visible && activeFilters.gender.length > 0) {
        if (meta) {
          const pGenders = getProductGenders(meta.gender, meta.title, meta.tags);
          const genderMatch = activeFilters.gender.some(g => pGenders.includes(g));
          if (!genderMatch) visible = false;
        }
      }

      // Category filter (Matches against product collections)
      if (visible && activeFilters.category.length > 0) {
        if (meta) {
          const pCols = (Array.isArray(meta.collections) && meta.collections.length > 0)
            ? meta.collections.map(c => c.toLowerCase())
            : [(meta.category || '').toLowerCase()];
          const catMatch = activeFilters.category.some(c => pCols.includes(c.toLowerCase()));
          if (!catMatch) visible = false;
        }
      }

      // Fragrance Family filter
      if (visible && activeFilters.fragrance_family.length > 0) {
        if (meta) {
          const fragMatch = activeFilters.fragrance_family.some(f => 
            meta.fragrance_family.toLowerCase().includes(f.toLowerCase())
          );
          if (!fragMatch) visible = false;
        }
      }

      // Material filter
      if (visible && activeFilters.material.length > 0) {
        if (meta) {
          const matMatch = activeFilters.material.some(m => 
            meta.material.toLowerCase().includes(m.toLowerCase())
          );
          if (!matMatch) visible = false;
        }
      }

      // Dispenser Type filter
      if (visible && activeFilters.dispenser_type.length > 0) {
        if (meta) {
          const dispMatch = activeFilters.dispenser_type.some(d => 
            meta.dispenser_type.toLowerCase().includes(d.toLowerCase())
          );
          if (!dispMatch) visible = false;
        }
      }

      // Availability filter
      if (visible && activeFilters.availability.length > 0) {
        if (meta) {
          if (activeFilters.availability.includes('in_stock') && !activeFilters.availability.includes('out_of_stock')) {
            if (!meta.in_stock) visible = false;
          } else if (activeFilters.availability.includes('out_of_stock') && !activeFilters.availability.includes('in_stock')) {
            if (meta.in_stock) visible = false;
          }
        }
      }

      // Price slider
      if (visible && activeFilters.max_price !== null && meta) {
        if (meta.price > activeFilters.max_price) visible = false;
      }

      card.style.display = visible ? '' : 'none';
      if (visible) visibleCount++;
    });

    const countEl = document.getElementById('ProductCount') || document.getElementById('ProductCountDesktop');
    if (countEl) {
      countEl.innerText = `${visibleCount} products`;
    }
  }

  function renderActiveChips() {
    const chipsAreas = document.querySelectorAll('.sfs-active-filters-bar');
    if (!chipsAreas.length) return;

    const chips = [];
    Object.keys(activeFilters).forEach(group => {
      if (Array.isArray(activeFilters[group])) {
        activeFilters[group].forEach(val => {
          let displayVal = val;
          if (val === 'in_stock') displayVal = 'In Stock';
          if (val === 'out_of_stock') displayVal = 'Out of Stock';
          chips.push(`
            <span class="sfs-active-tag" data-group="${group}" data-val="${val}">
              ${displayVal} &times;
            </span>
          `);
        });
      }
    });

    const slider = document.querySelector('.sfs-price-slider');
    const sliderMax = slider ? parseInt(slider.max) : null;
    if (activeFilters.max_price !== null && sliderMax && activeFilters.max_price < sliderMax) {
      chips.push(`
        <span class="sfs-active-tag" data-group="max_price" data-val="price">
          &le; ₹${activeFilters.max_price.toLocaleString()} &times;
        </span>
      `);
    }

    chipsAreas.forEach(area => {
      if (chips.length > 0) {
        area.style.display = 'flex';
        area.innerHTML = chips.join('') + `
          <span class="sfs-clear-all-btn" style="cursor:pointer; color:#999; font-size:12px; margin-left:8px; text-decoration:underline;">
            Clear all
          </span>
        `;

        area.querySelectorAll('.sfs-active-tag').forEach(tag => {
          tag.addEventListener('click', () => {
            const group = tag.dataset.group;
            const val = tag.dataset.val;

            if (group === 'max_price') {
              activeFilters.max_price = sliderMax;
              document.querySelectorAll('.sfs-price-slider').forEach(s => s.value = sliderMax);
              document.querySelectorAll('.sfs-price-display').forEach(d => d.innerText = `Up to ₹${sliderMax.toLocaleString()}`);
            } else {
              activeFilters[group] = activeFilters[group].filter(v => v !== val);
              document.querySelectorAll(`input[type="checkbox"][data-group="${group}"][data-val="${val}"]`).forEach(cb => cb.checked = false);
            }
            applyFiltersToGrid();
          });
        });

        const clearBtn = area.querySelector('.sfs-clear-all-btn');
        if (clearBtn) {
          clearBtn.addEventListener('click', () => {
            resetAllFilters();
          });
        }
      } else {
        area.style.display = 'none';
        area.innerHTML = '';
      }
    });
  }

  function resetAllFilters() {
    activeFilters.brand = [];
    activeFilters.category = [];
    activeFilters.gender = [];
    activeFilters.fragrance_family = [];
    activeFilters.material = [];
    activeFilters.dispenser_type = [];
    activeFilters.product_form = [];
    activeFilters.availability = [];
    activeFilters.max_price = null;

    document.querySelectorAll('.sfs-facet-checkbox').forEach(cb => cb.checked = false);

    const slider = document.querySelector('.sfs-price-slider');
    if (slider) {
      const max = parseInt(slider.max);
      document.querySelectorAll('.sfs-price-slider').forEach(s => s.value = max);
      document.querySelectorAll('.sfs-price-display').forEach(d => d.innerText = `Up to ₹${max.toLocaleString()}`);
    }

    applyFiltersToGrid();
  }

  function escapeHtml(str) {
    if (!str) return '';
    return str.replace(/[&<>"']/g, (m) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    }[m]));
  }

  /* -------------------------------------------------------------
   * Instant Predictive Search Autocomplete
   * ------------------------------------------------------------- */
  function initInstantSearch() {
    const searchInputs = document.querySelectorAll('input[type="search"], input[name="q"], .search__input');
    if (!searchInputs.length) return;

    searchInputs.forEach(input => {
      const wrapper = input.closest('form') || input.parentElement;
      if (!wrapper) return;

      if (window.getComputedStyle(wrapper).position === 'static') {
        wrapper.style.position = 'relative';
      }

      let dropdown = wrapper.querySelector('.sfs-autocomplete-container');
      if (!dropdown) {
        dropdown = document.createElement('div');
        dropdown.className = 'sfs-autocomplete-container';
        wrapper.appendChild(dropdown);
      }

      let debounceTimer = null;

      input.addEventListener('input', (e) => {
        clearTimeout(debounceTimer);
        const query = e.target.value.trim();

        if (query.length < 2) {
          dropdown.classList.remove('sfs-active');
          dropdown.innerHTML = '';
          return;
        }

        debounceTimer = setTimeout(() => {
          renderSearchResults(query, dropdown);
        }, 150);
      });

      document.addEventListener('click', (e) => {
        if (!wrapper.contains(e.target)) {
          dropdown.classList.remove('sfs-active');
        }
      });

      input.addEventListener('focus', () => {
        if (input.value.trim().length >= 2) {
          renderSearchResults(input.value.trim(), dropdown);
        }
      });
    });
  }

  async function renderSearchResults(query, dropdown) {
    try {
      const qLower = query.toLowerCase();

      let effectiveQuery = query;
      if (qLower === 'prda') effectiveQuery = 'Prada';
      if (qLower === 'armof' || qLower === 'armf') effectiveQuery = 'Armaf';
      if (qLower === 'davidof' || qLower === 'coolwater') effectiveQuery = 'Davidoff';
      if (qLower === 'carolina' || qLower === 'herrera') effectiveQuery = 'Carolina Herrera';

      const suggestions = POPULAR_SUGGESTIONS.filter(s =>
        s.toLowerCase().includes(effectiveQuery.toLowerCase())
      );

      let html = '';

      if (suggestions.length > 0) {
        html += `
          <div class="sfs-section-header">Popular Suggestions</div>
          <div class="sfs-suggestions-list">
            ${suggestions.map(s => `
              <a href="/search?q=${encodeURIComponent(s)}" class="sfs-suggestion-tag">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
                <span>${escapeHtml(s)}</span>
              </a>
            `).join('')}
          </div>
        `;
      }

      html += `
        <a href="/search?q=${encodeURIComponent(effectiveQuery)}" class="sfs-view-all-btn">
          View All Results &rarr;
        </a>
      `;

      dropdown.innerHTML = html;
      dropdown.classList.add('sfs-active');
    } catch (err) {
      console.warn('Parfumoo Filter: Predictive search error', err);
    }
  }

  // Auto-initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initCollectionFilters();
      initInstantSearch();
    });
  } else {
    initCollectionFilters();
    initInstantSearch();
  }
})();
